import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Combobox,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
  toast,
  type ComboboxOption,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { companySearchQueryOptions } from '@/features/programs/program-create/company-search-query';
import { FormMutationError, usePostForm } from '../forms-mutations';

/**
 * Form-create page — minimal metadata. The operator picks:
 *
 *   - **Compartilhado** toggle: when ON, the form is global (companyId
 *     becomes null) — every consultor can list/edit/duplicate it.
 *   - **Empresa** picker: only used when the form is NOT shared. Disabled
 *     and unvalidated while the toggle is on.
 *   - **Nome**: always required.
 *
 * On submit we POST the row with an empty SurveyJS definition (default at
 * the DB level) and navigate straight to the editor where the consultor
 * builds the survey. Two-step flow keeps this page tiny — the heavyweight
 * Survey Creator only mounts on /formularios/:id/editar.
 */
export default function FormCreatePage() {
  const navigate = useNavigate();
  // Workspace (área de trabalho) carried in the URL — preserved on every
  // navigation back to the list so the guard doesn't bounce to the Home.
  const { companyId: workspaceCompanyId } = useSearch({ from: '/_authenticated/formularios/novo' });
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companySearch, setCompanySearch] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; companyId?: string }>({});

  const companyQuery = useQuery(companySearchQueryOptions(companySearch));
  const postForm = usePostForm();

  const companyOptions: ComboboxOption[] = (companyQuery.data ?? []).map((c) => ({
    value: c.id,
    label: c.name,
  }));

  function validate(): boolean {
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Informe o nome do formulário';
    if (!isShared && !companyId) next.companyId = 'Selecione a empresa';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    // When shared, force companyId to null even if the operator had one
    // selected before flipping the toggle on.
    const effectiveCompanyId = isShared ? null : companyId;
    if (!isShared && !effectiveCompanyId) return;
    try {
      const created = await postForm.mutateAsync({
        companyId: effectiveCompanyId,
        name: name.trim(),
      });
      toast(isShared ? 'Formulário compartilhado criado.' : 'Formulário criado.', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      await navigate({
        to: '/formularios/$formId/editar',
        params: { formId: created.id },
        search: { companyId: workspaceCompanyId },
      });
    } catch (err) {
      const status = err instanceof FormMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Empresa não encontrada ou fora do seu escopo.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para criar formulários.');
      } else {
        toast.error('Não foi possível criar o formulário. Tente novamente.');
      }
    }
  }

  function handleBack() {
    void navigate({ to: '/formularios', search: { companyId: workspaceCompanyId } });
  }

  function handleSharedChange(checked: boolean) {
    setIsShared(checked);
    // Clear any company error when toggling on; the field's no longer required.
    if (checked && errors.companyId) {
      setErrors((prev) => ({ ...prev, companyId: undefined }));
    }
  }

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="forms" />

      <main className="flex flex-1 flex-col items-center gap-16 bg-card px-4 pt-8 pb-16 md:px-8">
        <div className="flex w-full max-w-[630px] flex-col gap-8">
          <header className="flex flex-col gap-2">
            <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
              Novo formulário
            </h1>
            <p className="text-sm text-muted-foreground">
              Defina o nome e a empresa. Você desenha as perguntas no próximo passo.
            </p>
          </header>

          <form
            className="flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
            noValidate
          >
            <FormField error={Boolean(errors.name)}>
              <FormFieldLabel className="text-foreground">Nome</FormFieldLabel>
              <Input
                value={name}
                placeholder="Ex.: Formulário de candidatura"
                variant={errors.name ? 'destructive' : 'default'}
                onChange={(e) => setName(e.target.value)}
                data-testid="form-create-name"
              />
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.name}
              </FormFieldError>
            </FormField>

            <FormField>
              <label className="flex cursor-pointer flex-row items-center gap-2 text-sm text-foreground">
                <Checkbox
                  checked={isShared}
                  onCheckedChange={(checked) => handleSharedChange(checked === true)}
                  data-testid="form-create-shared"
                />
                Compartilhar com todas as empresas
              </label>
              <FormFieldHint>
                Formulários compartilhados aparecem para qualquer consultor, sem vínculo com uma
                empresa específica.
              </FormFieldHint>
            </FormField>

            <FormField error={Boolean(errors.companyId)}>
              <FormFieldLabel className="text-foreground">
                {isShared ? 'Empresa (não aplicável)' : 'Empresa'}
              </FormFieldLabel>
              <Combobox
                value={isShared ? '' : (companyId ?? '')}
                options={companyOptions}
                onSelect={(v) => setCompanyId(v || null)}
                onSearchChange={setCompanySearch}
                placeholder={
                  isShared
                    ? 'Desabilitado para formulários compartilhados'
                    : 'Selecione uma empresa'
                }
                emptyMessage="Nenhuma empresa encontrada"
                error={Boolean(errors.companyId)}
                disabled={isShared}
                data-testid="form-create-company"
              />
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.companyId}
              </FormFieldError>
            </FormField>

            <div className="flex flex-row justify-between gap-2 pt-4">
              <Button type="button" variant="outline" size="lg" onClick={handleBack}>
                Voltar
              </Button>
              <Button
                type="submit"
                size="lg"
                loading={postForm.isPending}
                data-testid="form-create-submit"
              >
                Criar formulário
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
